import React from "react";
import { motion } from "framer-motion";
import {
  Truck, LifeBuoy, CarFront, Bike, Wrench, BatteryCharging,
  Container, Moon, AlertTriangle, Shovel, RotateCcw, Palmtree, Cpu,
} from "lucide-react";
import { fadeUp, fadeUpStagger, IN_VIEW, EASE_OUT_EXPO } from "@/lib/motion";

const SERVICES = [
  { icon: Truck, title: "Carro attrezzi H24", desc: "Servizio carro attrezzi attivo 24 ore su 24, ogni giorno dell'anno, in tutta Roma." },
  { icon: LifeBuoy, title: "Soccorso stradale", desc: "Soccorso stradale rapido a Roma e provincia, sempre disponibile." },
  { icon: AlertTriangle, title: "Recupero auto incidentate", desc: "Recupero auto incidentate con mezzi attrezzati per ogni situazione critica." },
  { icon: Bike, title: "Recupero moto", desc: "Soccorso e recupero moto e scooter dedicato a Roma e provincia." },
  { icon: CarFront, title: "Traino veicoli", desc: "Traino auto sicuro fino all'officina, al concessionario o a casa." },
  { icon: Wrench, title: "Auto in panne", desc: "Auto in panne? Riavviamo sul posto o trasportiamo il tuo veicolo." },
  { icon: BatteryCharging, title: "Batteria scarica", desc: "Avviamento di emergenza per batteria scarica direttamente sul posto." },
  { icon: Container, title: "Trasporto auto", desc: "Trasporto auto su richiesta per privati, officine e concessionari." },
  { icon: Moon, title: "Assistenza notturna", desc: "Assistenza stradale notturna a Roma: operativi anche nelle ore piccole." },
  { icon: RotateCcw, title: "Auto ribaltate", desc: "Recupero auto ribaltate o su un fianco dopo incidenti o uscite di strada." },
  { icon: Shovel, title: "Auto infossate", desc: "Recupero auto sprofondate in fango, terreno cedevole o buche stradali." },
  { icon: Palmtree, title: "Recupero in spiaggia", desc: "Auto incastrate nella sabbia a Ostia, Fregene, Fiumicino: torniamo in strada." },
  { icon: Cpu, title: "Blocco centralina", desc: "Auto ferma per blocco centralina o guasto elettronico: la portiamo in officina." },
];

const cardVariants = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: EASE_OUT_EXPO } },
};

export default function Services() {
  return (
    <section
      id="servizi"
      data-testid="services-section"
      className="relative py-20 md:py-28 bg-white overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.header
          initial="hidden"
          whileInView="visible"
          viewport={IN_VIEW}
          variants={fadeUpStagger}
          className="grid md:grid-cols-12 gap-6 md:gap-10 items-end"
        >
          <div className="md:col-span-7">
            <motion.p
              variants={fadeUp}
              className="text-brand-orange text-xs sm:text-sm font-bold tracking-[0.35em] uppercase"
            >
              — 02 / Servizi
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.02]"
            >
              Tutto ciò che serve{" "}
              <span className="text-brand-orange italic">subito</span>, in
              strada.
            </motion.h2>
          </div>
          <motion.p
            variants={fadeUp}
            className="md:col-span-5 text-muted-foreground text-base sm:text-lg leading-relaxed"
          >
            Un'unica squadra, una telefonata. Soccorso, recupero, traino e
            trasporto: pronti a partire 24/7 in tutta Roma e provincia.
          </motion.p>
        </motion.header>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={IN_VIEW}
          variants={fadeUpStagger}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4"
        >
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              variants={cardVariants}
              data-testid={`service-card-${i}`}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 240, damping: 22 }}
              className="group relative rounded-2xl border border-border bg-white p-5 hover:shadow-xl hover:border-navy/20 transition-all"
            >
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-navy text-white group-hover:bg-brand-orange transition-colors">
                <s.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-4 font-display font-extrabold text-lg text-navy">{s.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              <span className="absolute top-3 right-4 font-mono text-xs text-muted-foreground/60">
                0{i + 1}
              </span>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-4 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-orange to-transparent opacity-0 group-hover:opacity-100 transition"
              />
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
